// Safari Polyfills and Compatibility Fixes

import { logger } from './logger';

// Check if running in Safari
export const isSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('android');
};

// Initialize Safari-specific fixes
export const initSafariPolyfills = () => {
  if (!isSafari()) return;

  // Fix for ResizeObserver loop limit exceeded error in Safari
  if (window.ResizeObserver) {
    const resizeObserverErrorHandler = (e: ErrorEvent) => {
      if (e.message === 'ResizeObserver loop limit exceeded' || 
          e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        e.stopPropagation();
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    };
    
    window.addEventListener('error', resizeObserverErrorHandler);
  }

  // Fix for Safari's date input behavior
  const dateInputs = document.querySelectorAll<HTMLInputElement>('input[type="date"], input[type="datetime-local"]');
  dateInputs.forEach(input => {
    input.addEventListener('focus', () => {
      if (isSafari()) {
        input.setAttribute('type', 'text');
        input.setAttribute('placeholder', 'YYYY-MM-DD');
      }
    });
    
    input.addEventListener('blur', () => {
      if (isSafari() && !input.value) {
        input.setAttribute('type', 'date');
      }
    });
  });

  // Fix for Safari's smooth scrolling issues
  if ('scrollBehavior' in document.documentElement.style === false) {
    // Implement smooth scrolling polyfill
    const smoothScroll = (element: Element, to: number, duration: number) => {
      const start = element.scrollTop;
      const change = to - start;
      const startTime = performance.now();
      
      const animateScroll = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        element.scrollTop = start + change * easeInOutQuad(progress);
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };
      
      requestAnimationFrame(animateScroll);
    };
    
    const easeInOutQuad = (t: number): number => {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    };
    
    // Override scrollIntoView for smooth behavior
    Element.prototype.scrollIntoView = function(arg?: boolean | ScrollIntoViewOptions) {
      if (typeof arg === 'object' && arg?.behavior === 'smooth') {
        const element = this as Element;
        const rect = element.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const targetY = rect.top + scrollTop;
        
        smoothScroll(document.documentElement, targetY, 500);
      } else {
        // Fallback to native implementation
        Element.prototype.scrollIntoView.call(this, arg);
      }
    };
  }

  // Fix for Safari's flexbox gap support (versions < 14.1)
  const checkFlexGapSupport = () => {
    const flex = document.createElement('div');
    flex.style.display = 'flex';
    flex.style.gap = '1px';
    
    return flex.style.gap === '1px';
  };
  
  if (!checkFlexGapSupport()) {
    document.documentElement.classList.add('no-flexbox-gap');
  }

  // Fix for Safari's handling of focus-visible
  try {
    document.querySelector(':focus-visible');
  } catch (e) {
    // Add focus-visible polyfill class
    document.addEventListener('mousedown', () => {
      document.documentElement.classList.add('mouse-navigation');
    });
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        document.documentElement.classList.remove('mouse-navigation');
      }
    });
  }

  // Fix for Safari's handling of Intl.DateTimeFormat
  // Only apply if there's an actual issue with the format method
  if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
    try {
      // Test if DateTimeFormat works correctly
      const testFormatter = new Intl.DateTimeFormat('en-US');
      testFormatter.format(new Date());
    } catch (e) {
      // Only patch if there's an actual error
      logger.warn('Safari DateTimeFormat issue detected, applying workaround', 'SafariPolyfills');
      
      // Store original constructor
      const OriginalDateTimeFormat = Intl.DateTimeFormat;
      
      // Create a wrapper constructor
      (Intl as any).DateTimeFormat = function(locales?: string | string[], options?: Intl.DateTimeFormatOptions) {
        try {
          return new OriginalDateTimeFormat(locales, options);
        } catch (error) {
          // Fallback to a simple formatter
          return {
            format: (date: Date | number) => {
              const d = new Date(date);
              return d.toLocaleDateString(locales, options);
            },
            formatToParts: (date: Date | number) => {
              const d = new Date(date);
              return [{ type: 'literal', value: d.toLocaleDateString(locales, options) }];
            },
            resolvedOptions: () => ({
              locale: typeof locales === 'string' ? locales : 'en-US',
              ...options
            })
          };
        }
      };
      
      // Preserve prototype chain
      (Intl as any).DateTimeFormat.prototype = OriginalDateTimeFormat.prototype;
    }
  }

  // Add Safari-specific class to body for CSS targeting
  document.body.classList.add('safari-browser');
};

// Export utility function for Safari-safe array operations
export const safeArrayFrom = <T>(iterable: Iterable<T> | ArrayLike<T>): T[] => {
  try {
    return Array.from(iterable);
  } catch (e) {
    // Fallback for older Safari versions
    const arr: T[] = [];
    if ('length' in iterable) {
      for (let i = 0; i < iterable.length; i++) {
        arr.push(iterable[i]);
      }
    } else {
      for (const item of iterable as Iterable<T>) {
        arr.push(item);
      }
    }
    return arr;
  }
};

// Export utility for Safari-safe Object.fromEntries
export const safeObjectFromEntries = <T = any>(entries: Iterable<readonly [PropertyKey, T]>): { [k: string]: T } => {
  if (typeof Object.fromEntries === 'function') {
    return Object.fromEntries(entries);
  }
  
  // Polyfill for older Safari
  const obj: { [k: string]: T } = {};
  for (const [key, value] of entries) {
    obj[String(key)] = value;
  }
  return obj;
};

// Export utility for Safari-safe Promise.allSettled
export const safePromiseAllSettled = <T extends readonly unknown[] | []>(
  promises: T
): Promise<{ -readonly [P in keyof T]: PromiseSettledResult<Awaited<T[P]>> }> => {
  if (typeof Promise.allSettled === 'function') {
    return Promise.allSettled(promises);
  }
  
  // Polyfill for older Safari
  return Promise.all(
    promises.map(p =>
      Promise.resolve(p)
        .then(value => ({ status: 'fulfilled' as const, value }))
        .catch(reason => ({ status: 'rejected' as const, reason }))
    )
  ) as any;
};